import React from 'react';
import ContentPage from '../components/ContentPage';

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Play Buck Euchre",
  "description": "Complete rules guide for Buck Euchre, the popular trick-taking card game played with 3-4 players.",
  "step": [
    { "@type": "HowToStep", "name": "Setup", "text": "Gather 3 or 4 players and a standard 24-card Euchre deck (9, 10, J, Q, K, A in each suit)." },
    { "@type": "HowToStep", "name": "Deal", "text": "Deal 5 cards to each player. Flip the next card face-up to propose trump." },
    { "@type": "HowToStep", "name": "Call Trump", "text": "Players decide to accept or pass on the turned-up trump suit. If all pass, a second round allows naming any suit." },
    { "@type": "HowToStep", "name": "Play", "text": "Play 5 tricks. Lead any card, others must follow suit if possible. Highest trump or highest card of led suit wins the trick." },
    { "@type": "HowToStep", "name": "Score", "text": "Win 3 or more tricks to gain points. Fail to win 3 tricks and you are 'bucked' — receiving a penalty." }
  ]
};

const h2Style = { color: '#fff', fontSize: 26, marginTop: 40, marginBottom: 16 };
const h3Style = { color: '#fff', fontSize: 20, marginTop: 28, marginBottom: 12 };
const pStyle = { marginBottom: 16 };
const ulStyle = { marginBottom: 16, paddingLeft: 24 };
const liStyle = { marginBottom: 6 };

export default function BuckEuchreRulesPage() {
  return (
    <ContentPage
      title="Buck Euchre Rules - Complete Guide to Playing Buck Euchre"
      description="Learn the complete rules of Buck Euchre, the exciting trick-taking card game. Understand dealing, trump calling, card play, scoring, and the unique 'bucking' penalty."
      path="/buck-euchre-rules"
      jsonLd={jsonLd}
    >
      {/* Introduction */}
      <h2 style={h2Style}>What Is Buck Euchre?</h2>
      <p style={pStyle}>
        Buck Euchre is a thrilling trick-taking card game that transforms the traditional partnership-based Euchre into a
        cutthroat, every-player-for-themselves contest. Unlike standard Euchre where two teams of two compete cooperatively,
        Buck Euchre pits each player against every other player at the table. This individual format creates a tense,
        high-stakes atmosphere where every decision matters and a single misjudgment can cost you dearly.
      </p>
      <p style={pStyle}>
        What makes Buck Euchre stand out from other card games is the "bucking" penalty. When a player calls trump and fails
        to win enough tricks, they don't just miss out on points — they receive a steep penalty that can dramatically shift
        the standings. This risk-reward dynamic is the heart of the game and what keeps experienced players coming back
        hand after hand. You must carefully weigh the strength of your hand against the danger of being bucked before
        committing to a trump call.
      </p>
      <p style={pStyle}>
        Buck Euchre is popular across the Midwest United States and parts of Canada, particularly in regions where Euchre
        has deep cultural roots. It's an ideal game for smaller groups — perfect for three or four players who want a fast-paced,
        competitive card game without needing to find a fourth or fifth player to fill out teams. A typical game takes 20 to
        45 minutes, making it great for casual gatherings, pub nights, or tournament play.
      </p>

      {/* Players & Equipment */}
      <h2 style={h2Style}>Players and Equipment</h2>
      <h3 style={h3Style}>Number of Players</h3>
      <p style={pStyle}>
        Buck Euchre is designed for <strong>3 or 4 players</strong>. Each player competes independently — there are no
        teams or partnerships. The three-player version is the most traditional format, but the four-player variant adds
        another layer of competition and unpredictability to the game.
      </p>

      <h3 style={h3Style}>The Deck</h3>
      <p style={pStyle}>
        Buck Euchre uses a standard <strong>24-card Euchre deck</strong>. To create this deck from a standard 52-card deck,
        remove all cards ranked 2 through 8. The resulting deck contains four suits (hearts, diamonds, clubs, spades), each
        with six cards:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>9</strong> — the lowest card in each suit</li>
        <li style={liStyle}><strong>10</strong></li>
        <li style={liStyle}><strong>Jack</strong> — special role when trump is called (see Card Ranking below)</li>
        <li style={liStyle}><strong>Queen</strong></li>
        <li style={liStyle}><strong>King</strong></li>
        <li style={liStyle}><strong>Ace</strong> — the highest non-trump card in each off-suit</li>
      </ul>
      <p style={pStyle}>
        You can purchase pre-made Euchre decks at most game stores, or simply strip the low cards from any standard deck.
        Many experienced players keep a dedicated Euchre deck on hand since the reduced card count makes shuffling and
        dealing quicker.
      </p>

      {/* Dealing */}
      <h2 style={h2Style}>Dealing the Cards</h2>
      <p style={pStyle}>
        The dealer position rotates clockwise after each hand. Before each hand begins, the dealer shuffles the 24-card deck
        thoroughly and deals <strong>5 cards to each player</strong>. Cards are typically dealt in groups — a common pattern
        is to deal 3 cards to each player, then 2 more to each player (or 2 then 3). The exact dealing pattern may vary by
        house rules, but every player must end up with exactly 5 cards.
      </p>
      <p style={pStyle}>
        After dealing, the dealer flips the <strong>top card of the remaining deck face-up</strong> on the table. This
        turned-up card proposes the trump suit for the hand. For example, if the turned-up card is the King of Hearts,
        hearts is the proposed trump suit. This card is visible to all players and directly influences the first round of
        trump calling.
      </p>
      <p style={pStyle}>
        In a 3-player game, 15 cards are dealt and 1 is turned up, leaving 8 cards unseen in the kitty. In a 4-player game,
        20 cards are dealt and 1 is turned up, leaving only 3 unseen cards. This difference significantly affects strategy —
        in four-player Buck Euchre, you have much more information about where the remaining cards are likely to be.
      </p>

      {/* Trump Calling Round 1 */}
      <h2 style={h2Style}>Trump Calling: Round 1</h2>
      <p style={pStyle}>
        The trump-calling phase is where Buck Euchre's strategic depth truly shines. It occurs in two rounds, giving every
        player multiple chances to set (or avoid setting) the trump suit.
      </p>
      <p style={pStyle}>
        In the first round, players look at the turned-up card and decide whether they want that suit to be trump.
        Starting with the player to the <strong>left of the dealer</strong> and proceeding clockwise, each player either
        <strong>accepts</strong> the turned-up suit as trump or <strong>passes</strong>.
      </p>
      <p style={pStyle}>
        If a player accepts, the suit of the turned-up card becomes trump for the hand, and play begins immediately. The
        player who called trump is committing to win at least 3 of the 5 tricks — failing to do so results in the dreaded
        bucking penalty. Because of this, you should only call trump in the first round if your hand is strong in the
        proposed suit.
      </p>
      <p style={pStyle}>
        If <strong>all players pass</strong> in the first round, the turned-up card is turned face-down and the game moves
        to the second round of trump calling. The proposed suit from round one is no longer available for selection in round two.
      </p>

      {/* Trump Calling Round 2 */}
      <h2 style={h2Style}>Trump Calling: Round 2</h2>
      <p style={pStyle}>
        If no one accepts the turned-up suit, a second round of bidding begins. Again starting with the player to the left
        of the dealer, each player may <strong>name any suit except the one that was turned up</strong> in round one, or
        they may pass.
      </p>
      <p style={pStyle}>
        The first player to name a suit sets it as trump, and play begins. As with the first round, the caller is now on the
        hook to win at least 3 tricks or face the bucking penalty.
      </p>

      <h3 style={h3Style}>The "Next" Strategy</h3>
      <p style={pStyle}>
        One of the most important concepts in Buck Euchre trump calling is the <strong>"next" strategy</strong>. The "next"
        suit is the suit of the <strong>same color</strong> as the turned-up card. For example, if the turned-up card was a
        heart (red), then diamonds (also red) is the "next" suit. If the turned-up card was a spade (black), then clubs is next.
      </p>
      <p style={pStyle}>
        Why does next matter? When everyone passes on the turned-up suit, it often means no one holds the Jack of that suit
        (the right bower). Since the Jack of the same-color suit (the left bower) would also be trump in the turned-up suit,
        players who hold that Jack may have a strong incentive to call next. The left bower from the passed suit becomes the
        right bower in the next suit, giving the caller a powerful high trump. Experienced Buck Euchre players know that
        calling next is frequently the strongest play in the second round.
      </p>

      {/* Card Ranking */}
      <h2 style={h2Style}>Card Ranking in Buck Euchre</h2>
      <p style={pStyle}>
        Understanding card ranking is essential to playing Buck Euchre well. The ranking system differs between trump cards
        and off-suit cards, and the special role of the Jacks — called "bowers" — is one of the defining features of all
        Euchre variants.
      </p>

      <h3 style={h3Style}>Trump Suit Ranking (Highest to Lowest)</h3>
      <p style={pStyle}>
        When a suit is declared trump, the cards in that suit rank differently from normal. Here is the complete trump
        ranking from highest to lowest:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Right Bower</strong> — The Jack of the trump suit. This is the single highest card in the game.</li>
        <li style={liStyle}><strong>Left Bower</strong> — The Jack of the same-color suit as trump. For example, if hearts is trump, the Jack of Diamonds is the left bower. It is considered a trump card, not a diamond, for all purposes during the hand.</li>
        <li style={liStyle}><strong>Ace of Trump</strong></li>
        <li style={liStyle}><strong>King of Trump</strong></li>
        <li style={liStyle}><strong>Queen of Trump</strong></li>
        <li style={liStyle}><strong>10 of Trump</strong></li>
        <li style={liStyle}><strong>9 of Trump</strong></li>
      </ul>

      <h3 style={h3Style}>Off-Suit Ranking (Highest to Lowest)</h3>
      <p style={pStyle}>
        Cards in non-trump suits follow the standard ranking:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Ace</strong> (highest)</li>
        <li style={liStyle}><strong>King</strong></li>
        <li style={liStyle}><strong>Queen</strong></li>
        <li style={liStyle}><strong>Jack</strong> (unless it is the left bower, in which case it belongs to the trump suit)</li>
        <li style={liStyle}><strong>10</strong></li>
        <li style={liStyle}><strong>9</strong> (lowest)</li>
      </ul>
      <p style={pStyle}>
        A critical point: the left bower (Jack of the same color as trump) is always part of the trump suit during play. If
        clubs is trump, the Jack of Spades is a club for all purposes — you must play it when clubs are led if you have no
        other clubs, and it beats all trump cards except the right bower. This can surprise newer players who forget that the
        left bower has switched allegiance.
      </p>

      {/* Playing Tricks */}
      <h2 style={h2Style}>Playing Tricks</h2>
      <p style={pStyle}>
        Once trump is declared, the hand consists of <strong>5 tricks</strong>. The player to the left of the dealer leads
        the first trick by playing any card from their hand face-up on the table. Play then proceeds clockwise.
      </p>

      <h3 style={h3Style}>Following Suit</h3>
      <p style={pStyle}>
        When a card is led, all other players <strong>must follow suit</strong> if they are able. If the lead card is a
        diamond, you must play a diamond from your hand if you have one. Remember that the left bower belongs to the trump
        suit, not its printed suit — if hearts is trump and a diamond is led, the Jack of Hearts (left bower) is not a
        diamond and you are not required to play it.
      </p>
      <p style={pStyle}>
        If you <strong>cannot follow suit</strong> (you have no cards of the led suit), you may play any card from your hand.
        This includes the option to play a trump card — called "trumping in" or "ruffing." Trumping is often essential to
        winning the tricks you need, but spending your trump cards carelessly can leave you vulnerable in later tricks.
      </p>

      <h3 style={h3Style}>Winning a Trick</h3>
      <p style={pStyle}>
        The trick is won by the <strong>highest trump card</strong> played, if any trump was played. If no trump cards were
        played, the trick is won by the <strong>highest card of the suit that was led</strong>. The winner of each trick
        leads the next one, giving them the strategic advantage of choosing what suit to lead.
      </p>
      <p style={pStyle}>
        Winning the lead is valuable in Buck Euchre. It lets you set the pace — you can lead trump to draw out opponents'
        trump cards, or lead a strong off-suit Ace to collect an easy trick. Skilled players think several tricks ahead,
        planning which cards to play and when to maximize their trick count.
      </p>

      {/* Scoring */}
      <h2 style={h2Style}>Scoring in Buck Euchre</h2>
      <p style={pStyle}>
        Scoring is where Buck Euchre truly distinguishes itself from standard Euchre. The combination of individual play and
        harsh penalties creates dramatic swings that keep every player engaged until the very last hand.
      </p>

      <h3 style={h3Style}>Winning Tricks and Gaining Points</h3>
      <p style={pStyle}>
        After all 5 tricks have been played, each player counts the number of tricks they won. A player who wins
        <strong> 3 or more tricks</strong> earns <strong>+1 point for each trick</strong> they took. So winning 3 tricks
        earns +3, winning 4 tricks earns +4, and sweeping all 5 tricks earns +5.
      </p>

      <h3 style={h3Style}>The Bucking Penalty</h3>
      <p style={pStyle}>
        Here's where the tension comes in: any player who <strong>fails to win at least 3 tricks</strong> is
        <strong> "bucked."</strong> Being bucked means receiving a severe penalty — typically <strong>-5 points</strong>.
        In money games, the penalty may instead be the loss of the entry fee or ante. The bucking penalty applies to the
        player who called trump and to anyone else who played the hand and didn't reach the 3-trick threshold.
      </p>
      <p style={pStyle}>
        This asymmetric reward structure — modest gains for success, steep losses for failure — is the engine that drives
        Buck Euchre strategy. Calling trump is a calculated risk. You need to be confident you can win 3 tricks, because
        falling short doesn't just cost you the hand; it actively sets you back. One bad call can undo several hands' worth
        of careful play.
      </p>

      <h3 style={h3Style}>Score Examples</h3>
      <p style={pStyle}>
        Consider a 3-player hand where the trick results are: Player A wins 3 tricks, Player B wins 2 tricks, and Player C
        wins 0 tricks. The scoring would be:
      </p>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Player A:</strong> +3 points (won 3 or more tricks)</li>
        <li style={liStyle}><strong>Player B:</strong> -5 points (bucked — won fewer than 3 tricks)</li>
        <li style={liStyle}><strong>Player C:</strong> -5 points (bucked — won fewer than 3 tricks)</li>
      </ul>
      <p style={pStyle}>
        In a 4-player hand, multiple players can reach the 3-trick threshold — for instance, if one player wins 3 tricks
        and another wins 2, the first earns +3 while the second is bucked for -5. The mathematics of 5 tricks split among
        4 active players makes it very difficult for more than one player to avoid being bucked, which is part of what makes
        four-player Buck Euchre so intense.
      </p>

      {/* Sitting Out */}
      <h2 style={h2Style}>Sitting Out</h2>
      <p style={pStyle}>
        One of the distinctive features of Buck Euchre is the option to <strong>sit out</strong> a hand. After trump is
        called but before the first trick is played, any player (except the one who called trump) may choose to sit out.
        Sitting out means you don't play any cards for that hand, you cannot win any tricks, and you receive
        <strong> no score change</strong> — no points gained and no penalty.
      </p>
      <p style={pStyle}>
        Sitting out is a defensive strategy. If your hand is weak in the called trump suit and you're unlikely to win 3
        tricks, sitting out protects you from the -5 bucking penalty. However, you also give up the chance to earn points.
        Knowing when to sit and when to play is one of the most important skills in Buck Euchre.
      </p>
      <p style={pStyle}>
        Generally, you should consider sitting out when your hand contains few or no trump cards, no bowers, and no strong
        off-suit Aces. Conversely, even a moderately strong hand — with one or two trump cards and a side Ace — is often
        worth playing, especially if you suspect the tricks will be split among multiple opponents.
      </p>
      <p style={pStyle}>
        In four-player Buck Euchre, sitting out becomes even more strategically nuanced. With four active players competing
        for 5 tricks, it's nearly impossible for more than one player to reach 3 tricks. Experienced players sit more
        aggressively in the 4-player variant, choosing to play only when their hand is genuinely strong.
      </p>

      {/* Winning the Game */}
      <h2 style={h2Style}>Winning the Game</h2>
      <p style={pStyle}>
        In Buck Euchre, every player starts with a set point total (commonly 0 points, though some variations begin at a
        negative number). The goal is to be the <strong>first player to reach 0</strong> from a negative starting score, or
        in the alternative scoring format, the player with the <strong>highest score</strong> after a predetermined number of
        hands wins.
      </p>
      <p style={pStyle}>
        A common format is to play until one player reaches a target score, such as +10 or +15 points. The exact winning
        condition varies by house rules, but the fundamental principle is the same: accumulate more points than your opponents
        while avoiding the devastating bucking penalties that can send your score plummeting.
      </p>
      <p style={pStyle}>
        Some groups play "race to zero" style, where all players start at -25 (or another negative number) and race to reach
        0. Being bucked in this format pushes you 5 points further from the finish line, making each penalty feel especially
        punishing. This format tends to produce longer, more dramatic games with frequent lead changes.
      </p>
      <p style={pStyle}>
        End-game situations in Buck Euchre are particularly exciting. When one player is close to winning, the remaining
        players often make desperate trump calls or aggressive plays to try to catch up. Meanwhile, the leader may play
        conservatively — sitting out borderline hands rather than risking a bucking penalty that could cost them the game.
        This natural tension between aggression and caution is what makes Buck Euchre one of the most engaging card games
        you can play.
      </p>

      {/* Quick Reference */}
      <h2 style={h2Style}>Quick Reference Summary</h2>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Players:</strong> 3 or 4 (individual play, no teams)</li>
        <li style={liStyle}><strong>Deck:</strong> 24 cards (9, 10, J, Q, K, A in each suit)</li>
        <li style={liStyle}><strong>Deal:</strong> 5 cards each, turn up 1 card to propose trump</li>
        <li style={liStyle}><strong>Trump Calling:</strong> Two rounds — first for turned-up suit, second for any other suit</li>
        <li style={liStyle}><strong>Highest Card:</strong> Right bower (Jack of trump), then left bower (Jack of same color)</li>
        <li style={liStyle}><strong>Tricks:</strong> 5 per hand; must follow suit if able</li>
        <li style={liStyle}><strong>Scoring:</strong> 3+ tricks = +1 per trick; fewer than 3 = bucked (-5)</li>
        <li style={liStyle}><strong>Sit Out:</strong> Skip the hand for 0 points (no gain, no penalty)</li>
        <li style={liStyle}><strong>Winning:</strong> First to reach the target score wins</li>
      </ul>
      <p style={pStyle}>
        Now that you know the complete rules of Buck Euchre, it's time to put your knowledge to the test. Whether you're
        playing with friends around the kitchen table or competing online, the combination of strategy, risk management, and
        a little bit of luck makes every hand of Buck Euchre an exciting experience. Shuffle up and deal!
      </p>
    </ContentPage>
  );
}
